<template>
  <header class="container-header" ref="headerRef">
    <nav :class="domainClasses.nav">
      <div class="main-nav-left">
        <!--뒤로가기 버튼-->
        <button v-if="isUsingBackBtn" class="button-icon back-btn" @click="emit('back')">
          <IconChevronLeft class="icon-bigger" />
        </button>
        <!--로고-->
        <LogoMainNav :title="title" />
      </div>

      <div class="main-nav-right">
        <!--참가자 버튼-->
        <ButtonMainNavAttendees v-if="isVisibleButtonAttendees" />

        <!-- 가이드 버튼 -->
        <Tooltip v-if="isGuideBookEnabled" :tooltip-text="t('user_guide')" class="position-static web-guide-btn">
          <ButtonMainNavGuide />
        </Tooltip>

        <!--자막모드 버튼-->
        <Tooltip v-if="isVisibleButtonSubtitle" :tooltip-text="t('subtitle_view')" class="position-static">
          <ButtonMainNavSubtitle />
        </Tooltip>

        <!--스크린 버튼-->
        <Tooltip v-if="isVisibleButtonScreen" :tooltip-text="t('screen_view')" class="position-static">
          <ButtonMainNavScreen />
        </Tooltip>

        <!--공유 버튼-->
        <ButtonMainNavShare v-if="isVisibleButtonShare" />

        <!--MCS 버튼-->
        <ButtonMainNavMcs v-if="isVisibleSecretController" />

        <!--세팅 버튼-->
        <ButtonMainNavSetting v-if="isVisibleNavSetting" :domain="domain" :type-call="typeCall" />
        <!--크레딧 버튼-->
        <ButtonCreditInfo v-if="isUsingCredit" :using-auto-update="isUsingCredit" />
      </div>
    </nav>
  </header>
</template>

<script setup lang="ts">
import { computed, inject, Ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { storeToRefs } from 'pinia';

import ButtonCreditInfo from '@/components/domains/chat/common/ButtonCreditInfo.vue';
import ButtonMainNavAttendees from '@/components/domains/chat/common/ButtonMainNavAttendees.vue';
import ButtonMainNavGuide from '@/components/domains/chat/common/ButtonMainNavGuide.vue';
import ButtonMainNavMcs from '@/components/domains/chat/common/ButtonMainNavMcs.vue';
import ButtonMainNavScreen from '@/components/domains/chat/common/ButtonMainNavScreen.vue';
import ButtonMainNavSetting from '@/components/domains/chat/common/ButtonMainNavSetting.vue';
import ButtonMainNavShare from '@/components/domains/chat/common/ButtonMainNavShare.vue';
import LogoMainNav from '@/components/domains/chat/common/LogoMainNav.vue';
import IconChevronLeft from '@/components/icons/IconChevronLeft.vue';
import Tooltip from '@/components/shared/composite/Tooltip.vue';
import { useIdleLock } from '@/composables/domains/mobile/useIdleLock';
import useSize from '@/composables/shared/useSize';
import { useCallStore } from '@/stores/call';
import { useUserStore } from '@/stores/user';
import { TypeCall, TypeDomain } from '@/types/common';
import { ij_isVisibleSecretController } from '@/utils/injectionKeys.js';

import ButtonMainNavSubtitle from './ButtonMainNavSubtitle.vue';

const props = withDefaults(
  defineProps<{
    isMenuOpen: boolean;
    domain: TypeDomain;
    typeCall?: TypeCall;
    isUsingBackBtn?: boolean;
    isUsingCredit?: boolean;
  }>(),
  {
    isMenuOpen: true,
    domain: 'CHAT',
    typeCall: 'GENERAL',
    isUsingBackBtn: false,
    isUsingCredit: false,
  }
);

const isVisibleSecretController = inject(ij_isVisibleSecretController) as Ref<boolean>; // MCS 노출 여부

const callStore = useCallStore();
const userStore = useUserStore();
const { t } = useI18n();
const { initializeIdleLock, disUseIdleLock } = useIdleLock();

const { qrCodeInfo } = storeToRefs(callStore);

const { isMobileSize } = useSize('headerRef');

const emit = defineEmits<{ back: [] }>();

const domainClasses = computed<Record<string, string>>(() => {
  return (
    {
      CHAT: {
        nav: 'container-chat-header-content',
      },
      SELF_SERVICE: {
        nav: 'container-header-content',
      },
    }[props.domain] || {
      nav: 'container-chat-header-content',
    }
  );
});

// 헤더 타이틀
const title = computed<string>(() => {
  return (
    {
      AUDIENCE: t('audience_mode_title'),
      SINGLE: t('mic_input_settings_toggle_1v1'),
    }[props.typeCall] || ''
  );
});

const isVisibleNavSetting = computed<boolean>(() => {
  const isModeSingle = props.typeCall === 'SINGLE';
  return !isModeSingle && isMobileSize.value;
});

const isUserTypeHostOrSub = computed<boolean>(() => {
  return ['HOST', 'SUB'].includes(userStore.userInfo?.userType);
});

const isTypeCallMultilateral = computed<boolean>(() => {
  return ['GENERAL', 'MEETING', 'CONFERENCE'].includes(props.typeCall);
});

const hasAccessScreenOrSubtitle = computed<boolean>(() => {
  return isUserTypeHostOrSub.value && isTypeCallMultilateral.value;
});

const isVisibleButtonAttendees = computed<boolean>(() => {
  return qrCodeInfo.value && qrCodeInfo.value.options?.showAttendee && !isMobileSize.value;
});

const isGuideBookEnabled = computed<boolean>(() => {
  return props.domain === 'SELF_SERVICE' && !isMobileSize.value;
});

const isVisibleButtonScreen = computed<boolean>(() => {
  return hasAccessScreenOrSubtitle.value && qrCodeInfo.value?.options?.screenMode && !isMobileSize.value;
});

const isVisibleButtonSubtitle = computed<boolean>(() => {
  return hasAccessScreenOrSubtitle.value && qrCodeInfo.value?.options?.subtitleMode && !isMobileSize.value;
});

const isVisibleButtonShare = computed<boolean>(() => {
  return isTypeCallMultilateral.value && !isVisibleSecretController.value;
});

onMounted(async () => {
  await initializeIdleLock();
});

onUnmounted(async () => {
  await disUseIdleLock();
});
</script>

<style scoped lang="scss">
@import '@/assets/sass/layout/_selfservice.scss';

.main-nav-left {
  display: flex;
  align-items: center;
  padding-right: 0.25rem;
  flex: 1;
  min-width: 0;

  button {
    margin-left: -1rem;
  }
}

.main-nav-right {
  display: flex;
  width: fit-content;
  right: 1.25rem;
  gap: 0;
  align-items: center;
}

.nav-icon-container {
  position: relative;
  width: fit-content;
  min-width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  color: var(--color-gray-600);
  background-color: var(--color-gray-15);
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.75s ease;
}

.nav-icon-container p {
  margin: 0;
  display: none;
}

.nav-icon-container:hover,
.nav-icon-container.active {
  background-color: var(--color-gray-30);
}

@media (min-width: 768px) {
  .nav-icon-container {
    padding: 0 0.5rem 0 0.25rem;
  }

  .nav-icon-container p {
    display: inline;
  }
}

.tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
}

.position-static {
  position: static;
}

::v-deep(.button-credit) {
  @include respond-to(tablet) {
    margin-left: 0.5rem;
  }
}

@media (max-width: 884px) {
  .web-guide-btn {
    display: none;
  }
}
</style>
